import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  getNotificationCounts,
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type Notification,
  type NotificationCounts,
  type NotificationType
} from '@/api/communicationApi'
import { useAuth } from '@/hooks/useAuth'
import { onForegroundMessage, showNotification, isFirebaseConfigured } from '@/utils/firebase'

interface NotificationContextType {
  notifications: Notification[]
  counts: NotificationCounts
  isLoading: boolean
  error: string | null
  fetchNotifications: (page?: number) => Promise<void>
  refreshCounts: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  hasMore: boolean
  currentPage: number
}

const defaultCounts: NotificationCounts = {
  total: 0,
  unread: 0,
  byType: {
    announcement: 0,
    event_reminder: 0,
    attendance_alert: 0,
    transport_update: 0,
    academic_update: 0,
    fee_reminder: 0,
    library_notice: 0,
    system: 0
  } as Record<NotificationType, number>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [counts, setCounts] = useState<NotificationCounts>(defaultCounts)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const refreshCounts = useCallback(async () => {
    if (!user) return
    try {
      const newCounts = await getNotificationCounts()
      setCounts(newCounts)
    } catch (err) {
      console.error('Failed to fetch notification counts:', err)
    }
  }, [user])

  const fetchNotifications = useCallback(
    async (page = 1) => {
      if (!user) return
      setIsLoading(true)
      setError(null)
      try {
        const response = await getNotifications({ page, limit: 20 })
        if (page === 1) {
          setNotifications(response.notifications)
        } else {
          setNotifications((prev) => [...prev, ...response.notifications])
        }
        setCurrentPage(page)
        setHasMore(response.pagination.currentPage < response.pagination.totalPages)
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch notifications')
      } finally {
        setIsLoading(false)
      }
    },
    [user]
  )

  const markAsRead = useCallback(async (id: string) => {
    try {
      await markNotificationAsRead(id)
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, status: 'read' as const, readAt: new Date().toISOString() } : n
        )
      )
      setCounts((prev) => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1)
      }))
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead()
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, status: 'read' as const, readAt: new Date().toISOString() }))
      )
      setCounts((prev) => ({ ...prev, unread: 0 }))
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err)
    }
  }, [])

  // Initial load and polling
  useEffect(() => {
    if (user) {
      fetchNotifications(1)
      refreshCounts()

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        refreshCounts()
      }, 30000)

      return () => clearInterval(interval)
    }
  }, [user, fetchNotifications, refreshCounts])

  // Listen for foreground push notifications from Firebase
  useEffect(() => {
    if (!user || !isFirebaseConfigured()) {
      return
    }

    console.log('[NotificationContext] Setting up foreground message listener')

    const unsubscribe = onForegroundMessage((payload) => {
      console.log('[NotificationContext] ====== PUSH NOTIFICATION RECEIVED ======')
      console.log('[NotificationContext] Full payload:', JSON.stringify(payload, null, 2))
      console.log('[NotificationContext] Notification:', payload.notification)
      console.log('[NotificationContext] Data:', payload.data)
      console.log('[NotificationContext] =========================================')

      // Show browser notification
      const title = payload.notification?.title || 'SmartTag School'
      const body = payload.notification?.body || 'You have a new notification'

      console.log('[NotificationContext] Showing notification:', { title, body })

      showNotification(title, {
        body,
        icon: '/logo192.png',
        tag: payload.data?.type || 'smarttag-notification',
      })

      // Refresh notification counts when we receive a push
      refreshCounts()
    })

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [user, refreshCounts])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        counts,
        isLoading,
        error,
        fetchNotifications,
        refreshCounts,
        markAsRead,
        markAllAsRead,
        hasMore,
        currentPage
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
